import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  colors,
  MAX_CONTENT_WIDTH,
  radii,
  screenPadding,
  shadows,
  spacing,
} from '../../theme';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

/** Whole rupees, grouped - `Intl` is not reliably present on Android JSC. */
const rupees = (value: number) =>
  '₹' + String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

type Props = {
  /** Total items, counting quantities. */
  count: number;
  /** Sum of the lines, before any fees. */
  total: number;
  onPress: () => void;
  /**
   * Set when nothing is drawn below this bar.
   *
   * On Home the tab bar sits underneath and owns the safe-area inset, so the
   * bar needs none of its own. On a screen with no tab bar this is the last
   * thing on the page, and without the inset it lands under the gesture bar
   * or the nav buttons.
   */
  insetBottom?: boolean;
  /**
   * Hides the bar for now. Omit and no cross is drawn.
   *
   * ---------------------------------------------------------------------------
   * THIS DISMISSES. IT DOES NOT EMPTY THE CART.
   * ---------------------------------------------------------------------------
   * A cross on this bar used to reveal a red "Dismiss" that threw the basket
   * away, and the note below records why that was removed: on a strip that
   * follows you down every screen, a cross reads as "close this" and not as
   * "delete everything I chose", so people emptied their cart by tapping what
   * they thought was a close button.
   *
   * That reading is the right one, so this control now does what it looks like
   * - it closes the bar. The basket is untouched, the badge on the Cart tab
   * still shows the count, and the bar returns on the next change to the cart.
   * Emptying still belongs on the cart screen, where the lines being removed
   * are on screen to see.
   */
  onDismiss?: () => void;
};

/**
 * The standing reminder that something is in the cart.
 *
 * Sits directly above the tab bar and is laid out in the column rather than
 * floated over it, so it pushes content up instead of covering the last row.
 * Renders nothing on an empty cart - a permanent bar reading "0 items" is
 * furniture that says nothing.
 *
 * ---------------------------------------------------------------------------
 * ONE CONTROL, AND IT GOES TO THE CART
 * ---------------------------------------------------------------------------
 * The bar used to slide aside - by swipe, or by a cross on its right - to
 * uncover a red "Dismiss" that emptied the basket. That is gone. It put a
 * destructive action inside the one element that follows you down every
 * screen, competing for the same strip as the control people actually want,
 * and the cross read as "close this bar" rather than "throw away everything
 * you have chosen". Emptying a cart belongs on the cart screen, where the
 * lines being removed are visible.
 *
 * So: the body of the bar is one press, and that press opens the cart. No
 * swipe handling and no revealed drawer.
 *
 * The cross is back, but as a plain dismiss rather than the destructive action
 * it used to hide - see `onDismiss`. It sits outside the main pressable so a
 * tap on it cannot also open the cart.
 */
function CartBar({
  count,
  total,
  onPress,
  onDismiss,
  insetBottom = false,
}: Props) {
  const insets = useSafeAreaInsets();

  if (count === 0) {
    return null;
  }

  return (
    <View
      style={[
        styles.wrap,
        // The same expression the tab bar uses, so the bar sits at the same
        // height off the system navigation on both kinds of screen.
        insetBottom
          ? { paddingBottom: Math.max(insets.bottom, spacing.md) + spacing.sm }
          : null,
      ]}
      pointerEvents="box-none"
    >
      <View style={styles.shadow}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`View cart, ${count} item${
            count > 1 ? 's' : ''
          }, ${rupees(total)}`}
          onPress={onPress}
          style={({ pressed }) => [styles.bar, pressed ? styles.pressed : null]}
        >
          <View style={styles.badge}>
            <Icon name="cart" size={18} color={colors.onPrimary} />
          </View>

          <View style={styles.body}>
            <Text variant="labelSm" color={colors.inkInverse}>
              {count} {count > 1 ? 'services' : 'service'} added
            </Text>
            <Text variant="caption" color={colors.inkInverseMuted}>
              {rupees(total)} · fees at checkout
            </Text>
          </View>

          <View style={styles.go}>
            <Text variant="labelSm" color={colors.inkInverse}>
              View cart
            </Text>
            <Icon
              name="arrowRight"
              size={15}
              color={colors.inkInverse}
              strokeWidth={2}
            />
          </View>
        </Pressable>

        {/*
          A sibling of the bar, not a child of it.

          Nested inside, a press would run this AND the bar's own `onPress` as
          it bubbled - dismissing the bar and opening the cart at once. Drawn
          absolutely over the bar's right edge instead, so the two targets
          cannot overlap.
        */}
        {onDismiss ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Hide cart bar"
            onPress={onDismiss}
            hitSlop={10}
            style={({ pressed }) => [
              styles.dismiss,
              pressed ? styles.pressed : null,
            ]}
          >
            <Icon
              name="xCircle"
              size={18}
              color={colors.inkInverseMuted}
              strokeWidth={2}
            />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  /* Held to a phone's width and centred on a tablet, so the bar stays over
     the tab bar it belongs with rather than stretching past it. */
  wrap: {
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH + screenPadding * 2,
    alignSelf: 'center',
    paddingHorizontal: screenPadding,
    paddingBottom: spacing.sm,
  },
  /*
   * The shadow lives on its own view, and the bar inside it carries the
   * rounding.
   *
   * `overflow: 'hidden'` and a shadow on the same view cancel out on iOS - the
   * view clips its own shadow away. There is nothing to clip any more now the
   * sliding drawer is gone, so the bar simply rounds its own corners.
   */
  shadow: {
    borderRadius: radii.md,
    ...shadows.md,
  },
  /*
   * Dark, and a step larger than it was.
   *
   * The light bar read as another card in a page made of light cards, which
   * is the one thing it must not be - it is the only element that follows you
   * down the page, and it has to look like it belongs to the app rather than
   * to the content. No hairline: a border on a dark surface over a light page
   * only muddies its edge.
   */
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceInverse,
  },
  badge: {
    width: 34,
    height: 34,
    borderRadius: radii.sm,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
  },
  go: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    /* Room for the dismiss cross drawn over the bar's right edge, so "View
       cart →" stops short of it rather than running underneath. */
    marginRight: spacing.lg,
  },
  /*
   * The dismiss cross, over the bar's top-right rather than in its row.
   *
   * Absolute so it is a sibling of the bar's pressable - see the render. In
   * the row it would either be inside that pressable (and fire both actions)
   * or push "View cart" leftwards on every screen for a control that is
   * secondary to it.
   */
  dismiss: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: spacing.sm,
  },
  pressed: {
    opacity: 0.8,
  },
});

export default React.memo(CartBar);
